/**
 * PerfPill — a compact always-visible performance control near the transport.
 *
 * Shows the live FPS (from window.__synapsePerf) and a 3-way mode selector that
 * drives object density through the shared perf budget:
 *   • Auto        — the adaptive governor lowers density only when FPS drops
 *                   (~below 60) and restores it when there's headroom.
 *   • Performance — forces a short draw distance (few objects, max framerate).
 *   • Quality     — forces the full runway (many objects).
 *
 * The two manual modes pin the note + obstacle draw distances via the same
 * setManual*View hooks the adaptive governor respects; Auto clears them.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useEditorStore } from '../../store/editorStore';
import type { PerfMode } from '../../store/editorStore';
import {
  setManualNoteView, setManualObstacleView, setManualRenderScale, startRenderScaleAt,
} from '../../perf/adaptiveBudget';
import { sessionIsSoftwareGL, sessionIsIntegratedGPU } from '../../perf/perfTelemetry';
import { BUILD_INFO, BUILD_LABEL } from '../../buildInfo';
import { color, font } from '../ui/theme';

const MODES: { key: PerfMode; label: string; title: string }[] = [
  { key: 'potato', label: 'Potato', title: 'Potato — for weak machines (integrated graphics): a steady 30fps, no antialiasing, no particles or waveform, and a still minimap while playing. Resolution is not fixed — it settles at the sharpest setting your machine can hold 30fps at. Note placement and audio stay exact.' },
  { key: 'performance', label: 'Perf', title: 'High Performance — fewer objects (short draw distance) AND a lower render resolution, for the highest framerate on any GPU.' },
  { key: 'auto', label: 'Auto', title: 'Auto — lowers density (then resolution) only when FPS drops below ~60, and restores both when there is headroom.' },
  { key: 'quality', label: 'Quality', title: 'High Quality — full object density and native resolution.' },
];

export default function PerfPill() {
  const perfMode = useEditorStore(s => s.perfMode);
  const setPerfMode = useEditorStore(s => s.setPerfMode);
  // The active preset's knobs — per-preset USER SETTINGS (Performance tab in
  // Settings), defaults in DEFAULT_PERFORMANCE. Values that used to be the
  // PERF_MODE_BEATS / MODE_RENDER_SCALE / POTATO constants now live there.
  const cfg = useEditorStore(s => s.performanceSettings[s.perfMode]);
  const [fps, setFps] = useState(60);
  // Software-WebGL warning (dismissible). The flag is set when telemetry
  // inits inside the canvas, which happens after this pill mounts — so it's
  // read on the same 400ms poll as the FPS instead of once at mount.
  const [softGL, setSoftGL] = useState(false);
  const [softGLDismissed, setSoftGLDismissed] = useState(
    () => { try { return sessionStorage.getItem('synapse_softgl_dismissed') === '1'; } catch { return false; } },
  );
  // Integrated-GPU + genuinely slow → the user may have a discrete GPU sitting
  // idle (telemetry showed exactly that). Only nags after ~10s of sustained
  // low FPS, so a brief hitch never triggers it.
  const [igpuSlow, setIgpuSlow] = useState(false);
  const slowTicks = useRef(0);
  const autoPicked = useRef(false);

  // Apply the active preset to the shared perf budget. Deliberately split into
  // THREE effects: a single effect's cleanup would run between every re-run, so
  // editing any knob in the settings panel would release + re-pin everything —
  // including setManualRenderScale(null), which re-seeds the ladder and clears
  // its ceiling. Split, each knob only touches its own setter.

  // Draw distance: null = adaptive (the governor tunes it by framerate).
  useEffect(() => {
    setManualNoteView(cfg.drawDistanceBeats);
    setManualObstacleView(cfg.drawDistanceBeats);
  }, [cfg.drawDistanceBeats]);

  // Render scale pin / ladder seed. Debounced: SliderRow fires per mousemove and
  // every distinct dpr value reallocates the drawing buffer (a blank frame), so
  // a drag would flash-storm the canvas without it.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setManualRenderScale(cfg.renderScale);
      // When the ladder is adaptive, optionally hand it a starting rung instead
      // of whatever the previous mode drifted to (potato does this by default).
      if (cfg.renderScale == null && cfg.ladderSeed != null) startRenderScaleAt(cfg.ladderSeed);
    }, 200);
    return () => window.clearTimeout(id);
  }, [cfg.renderScale, cfg.ladderSeed]);

  // Release every pin on unmount ONLY (editor switch) — never between re-runs.
  useEffect(() => {
    return () => { setManualNoteView(null); setManualObstacleView(null); setManualRenderScale(null); };
  }, []);

  // Poll the live FPS probe (light, off the render loop).
  useEffect(() => {
    const id = window.setInterval(() => {
      const p = (window as unknown as { __synapsePerf?: { fps: number } }).__synapsePerf;
      if (p && typeof p.fps === 'number') setFps(p.fps);
      setSoftGL(sessionIsSoftwareGL());
      if (sessionIsIntegratedGPU() && p && typeof p.fps === 'number') {
        slowTicks.current = p.fps < 45 ? slowTicks.current + 1 : 0;
        if (slowTicks.current >= 25) setIgpuSlow(true);   // ~10s under 45fps
      }
      // First run on weak hardware → start in Potato instead of leaving the
      // user at 27fps until they discover the pill. Only ONCE (a flag, not the
      // mode itself), so switching away always sticks.
      if (!autoPicked.current && (sessionIsIntegratedGPU() || sessionIsSoftwareGL())) {
        autoPicked.current = true;
        try {
          if (!localStorage.getItem('synapse_perf_mode')
              && !localStorage.getItem('synapse_potato_autopicked')) {
            localStorage.setItem('synapse_potato_autopicked', '1');
            useEditorStore.getState().setPerfMode('potato');
          }
        } catch { /* storage blocked — skip the auto-pick */ }
      }
    }, 400);
    return () => window.clearInterval(id);
  }, []);

  const fpsColor = fps >= 55 ? '#34D399' : fps >= 40 ? '#FBBF24' : '#F87171';

  return (
    // Column: the pill itself, with the build version tucked under it —
    // reviewers need to know which build a bug report came from, without it
    // taking up room in the pill.
    <div style={stack}>
      <div style={wrap} role="group" aria-label="Performance">
        <span style={{ ...fpsBox, color: fpsColor }} title={`${fps} FPS`}>
          <span style={dot(fpsColor)} />
          <span style={fpsNum}>{fps}</span>
          <span style={fpsUnit}>FPS</span>
        </span>
        <span style={divider} />
        <span style={seg}>
          {MODES.map(m => {
            const on = perfMode === m.key;
            return (
              <button
                key={m.key}
                onClick={(e) => { (e.currentTarget as HTMLButtonElement).blur(); setPerfMode(m.key); }}
                title={m.title}
                style={{ ...segBtn, ...(on ? segOn : null) }}
              >
                {m.label}
              </button>
            );
          })}
        </span>
      </div>
      <span
        style={buildStamp}
        title={`Build ${BUILD_INFO.commit}${BUILD_INFO.branch ? ` (${BUILD_INFO.branch})` : ''} — built ${new Date(BUILD_INFO.builtAt).toLocaleString()}`}
      >
        {BUILD_LABEL}
      </span>
      {!softGL && igpuSlow && !softGLDismissed && (
        <div style={softGLWarn} role="alert">
          <span>
            ⚠ The editor is running on your <strong>integrated graphics</strong>. If this
            machine also has a dedicated GPU, point the browser at it: Windows Settings →
            Display → Graphics → your browser → Options → <strong>High performance</strong>
            {' '}(desktop: make sure the monitor cable goes to the graphics card, not the
            motherboard). Then relaunch the browser. Meanwhile, the <strong>Auto</strong> mode
            above will keep the framerate up.
          </span>
          <button
            onClick={() => {
              setSoftGLDismissed(true);
              try { sessionStorage.setItem('synapse_softgl_dismissed', '1'); } catch { /* ignore */ }
            }}
            style={softGLDismissBtn}
            title="Hide for this session"
          >
            ×
          </button>
        </div>
      )}
      {softGL && !softGLDismissed && (
        <div style={softGLWarn} role="alert">
          <span>
            ⚠ Your browser is rendering WITHOUT the graphics card (software mode) — that's
            why it's slow. Enable "Use graphics acceleration" in your browser's System
            settings and relaunch it.
          </span>
          <button
            onClick={() => {
              setSoftGLDismissed(true);
              try { sessionStorage.setItem('synapse_softgl_dismissed', '1'); } catch { /* ignore */ }
            }}
            style={softGLDismissBtn}
            title="Hide for this session"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const stack: React.CSSProperties = {
  display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 3,
};

/** Software-WebGL banner — loud on purpose: nothing we tune can outrun a CPU
 *  rasterizer, so the ONE useful thing is telling the user how to fix it. */
const softGLWarn: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 8, maxWidth: 420,
  padding: '7px 10px', borderRadius: 10, marginTop: 2,
  background: 'rgba(239, 68, 68, 0.14)', border: '1px solid rgba(239, 68, 68, 0.45)',
  color: '#FCA5A5', fontFamily: font.sans, fontSize: 11, lineHeight: 1.4, textAlign: 'left',
};
const softGLDismissBtn: React.CSSProperties = {
  flexShrink: 0, background: 'transparent', border: 'none', cursor: 'pointer',
  color: '#FCA5A5', fontSize: 14, lineHeight: 1, padding: 2,
};

/** Version stamp under the pill — deliberately quiet (small, low contrast). */
const buildStamp: React.CSSProperties = {
  fontFamily: MONO, fontSize: 9, letterSpacing: 0.3,
  color: color.inkFaint, opacity: 0.75, userSelect: 'text',
  textShadow: '0 1px 3px rgba(0,0,0,0.6)',
};

const wrap: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  padding: '5px 8px 5px 10px', borderRadius: 999,
  background: 'oklch(0.14 calc(0.02 * var(--tint-c)) var(--tint-h) / 0.9)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.28)',
  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
  fontFamily: font.sans, userSelect: 'none',
  boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
};
const fpsBox: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'baseline', gap: 4,
  fontFamily: MONO, fontVariantNumeric: 'tabular-nums',
};
const dot = (c: string): React.CSSProperties => ({
  width: 7, height: 7, borderRadius: '50%', background: c, alignSelf: 'center',
  boxShadow: `0 0 6px ${c}`,
});
const fpsNum: React.CSSProperties = { fontSize: 15, fontWeight: 700, lineHeight: 1 };
const fpsUnit: React.CSSProperties = { fontSize: 8.5, fontWeight: 700, letterSpacing: 0.5, opacity: 0.85 };
const divider: React.CSSProperties = {
  width: 1, height: 16, background: 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.22)',
};
const seg: React.CSSProperties = {
  display: 'inline-flex', gap: 2, background: 'oklch(0.10 0 0 / 0.4)', borderRadius: 999, padding: 2,
};
const segBtn: React.CSSProperties = {
  border: 'none', background: 'transparent', cursor: 'pointer',
  fontFamily: font.sans, fontSize: 11, fontWeight: 600, color: color.inkFaint,
  padding: '3px 9px', borderRadius: 999, transition: 'all 0.14s ease', lineHeight: 1.25,
};
const segOn: React.CSSProperties = {
  background: 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.35), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.22))',
  color: color.accent,
  boxShadow: 'inset 0 0 0 1px oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
};
